import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { collection, doc } from 'firebase/firestore';
import { db } from '../firebase';
import {
  LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
} from 'recharts';
import './AdminHonorRollDetailView.css';
import { dataCacheService } from '../services/DataCacheService';

const TIER_CONFIG = {
  highHonorRoll: {
    label: 'High Honor Roll',
    color: '#f59e0b',
    bgColor: '#fef3c7',
    icon: '🏆',
    min: 95,
  },
  principalsList: {
    label: "Principal's List",
    color: '#15803d',
    bgColor: '#dcfce7',
    icon: '⭐',
    min: 90,
    max: 94.9,
  },
  honorRoll: {
    label: 'Honor Roll',
    color: '#22c55e',
    bgColor: '#dcfce7',
    icon: '🎖️',
    min: 85,
    max: 89.9,
  },
  none: {
    label: 'No Distinction',
    color: '#666',
    bgColor: '#f5f5f5',
    icon: '📋',
    min: 0,
    max: 84.9,
  },
};

function getLetterGrade(percentage) {
  if (percentage >= 93) return 'A';
  if (percentage >= 90) return 'A-';
  if (percentage >= 87) return 'B+';
  if (percentage >= 83) return 'B';
  if (percentage >= 80) return 'B-';
  if (percentage >= 77) return 'C+';
  if (percentage >= 73) return 'C';
  if (percentage >= 70) return 'C-';
  if (percentage >= 67) return 'D+';
  if (percentage >= 63) return 'D';
  if (percentage >= 60) return 'D-';
  return 'F';
}

function getGpaFromPercentage(percentage) {
  if (percentage >= 93) return 4.0;
  if (percentage >= 90) return 3.7;
  if (percentage >= 87) return 3.3;
  if (percentage >= 83) return 3.0;
  if (percentage >= 80) return 2.7;
  if (percentage >= 77) return 2.3;
  if (percentage >= 73) return 2.0;
  if (percentage >= 70) return 1.7;
  if (percentage >= 67) return 1.3;
  if (percentage >= 63) return 1.0;
  if (percentage >= 60) return 0.7;
  return 0.0;
}

function formatDate(dateValue) {
  if (!dateValue) return 'N/A';
  try {
    const date = dateValue.toDate ? dateValue.toDate() : new Date(dateValue);
    return date.toLocaleDateString();
  } catch {
    return String(dateValue);
  }
}

function getHonorRollTier(average) {
  if (average >= 95) return 'highHonorRoll';
  if (average >= 90) return 'principalsList';
  if (average >= 85) return 'honorRoll';
  return 'none';
}

function AdminHonorRollDetailView({ adminData, studentId, onBack }) {
  const [studentData, setStudentData] = useState(null);
  const [grades, setGrades] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [shareStatus, setShareStatus] = useState('');

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const ck = (p) => `${adminData?.schoolId || 'global'}:${p}`;

      const studentDoc = await dataCacheService.getDoc(ck(`students/${studentId}`), doc(db, 'students', studentId));
      if (studentDoc) setStudentData(studentDoc);

      const classesData = await dataCacheService.getDocs(ck('classes'), collection(db, 'classes'));

      const allGrades = [];

      await Promise.all(classesData.map(async (cls) => {
        const classStudents = await dataCacheService.getDocs(ck(`classes/${cls.id}/students`), collection(db, 'classes', cls.id, 'students'));
        if (!classStudents.some(d => d.id === studentId)) return;

        const classAssignments = await dataCacheService.getDocs(ck(`classes/${cls.id}/assignments`), collection(db, 'classes', cls.id, 'assignments'));

        await Promise.all(classAssignments.map(async (assignmentDoc) => {
          const assignmentGrades = await dataCacheService.getDocs(
            ck(`classes/${cls.id}/assignments/${assignmentDoc.id}/grades`),
            collection(db, 'classes', cls.id, 'assignments', assignmentDoc.id, 'grades')
          );
          assignmentGrades.forEach(gradeData => {
            if (gradeData.studentId === studentId) {
              allGrades.push({
                ...gradeData,
                classId: cls.id,
                assignmentId: assignmentDoc.id,
                assignmentTitle: assignmentDoc.title || 'Untitled',
                subject: assignmentDoc.subject || 'General',
                dueDate: assignmentDoc.dueDate || null,
              });
            }
          });
        }));
      }));

      setGrades(allGrades);
    } catch (err) {
      console.error('Error loading honor roll data:', err);
      setError('Failed to load student data');
    } finally {
      setLoading(false);
    }
  }, [studentId, adminData]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const validGrades = useMemo(() => {
    return grades
      .filter(g => g.score != null && g.maxScore != null)
      .map(g => {
        const percentage = parseFloat(((g.score / g.maxScore) * 100).toFixed(1));
        return {
          ...g,
          percentage,
          letterGrade: getLetterGrade(percentage),
          gpa: getGpaFromPercentage(percentage),
        };
      })
      .sort((a, b) => {
        const dateA = a.dueDate ? (a.dueDate.toDate ? a.dueDate.toDate().getTime() : new Date(a.dueDate).getTime()) : 0;
        const dateB = b.dueDate ? (b.dueDate.toDate ? b.dueDate.toDate().getTime() : new Date(b.dueDate).getTime()) : 0;
        return dateA - dateB;
      });
  }, [grades]);

  const overallAverage = useMemo(() => {
    if (validGrades.length === 0) return 0;
    const sum = validGrades.reduce((acc, g) => acc + g.percentage, 0);
    return parseFloat((sum / validGrades.length).toFixed(1));
  }, [validGrades]);

  const overallGpa = useMemo(() => {
    if (validGrades.length === 0) return 0;
    const sum = validGrades.reduce((acc, g) => acc + g.gpa, 0);
    return parseFloat((sum / validGrades.length).toFixed(2));
  }, [validGrades]);

  const tier = useMemo(() => getHonorRollTier(overallAverage), [overallAverage]);
  const tierConfig = TIER_CONFIG[tier];

  const subjectBreakdown = useMemo(() => {
    const bySubject = {};
    validGrades.forEach(g => {
      const subject = g.subject || 'General';
      if (!bySubject[subject]) {
        bySubject[subject] = { total: 0, count: 0, gpaTotal: 0 };
      }
      bySubject[subject].total += g.percentage;
      bySubject[subject].gpaTotal += g.gpa;
      bySubject[subject].count++;
    });
    return Object.entries(bySubject)
      .map(([name, data]) => ({
        name,
        average: parseFloat((data.total / data.count).toFixed(1)),
        gpa: parseFloat((data.gpaTotal / data.count).toFixed(2)),
        letterGrade: getLetterGrade(data.total / data.count),
        count: data.count,
        qualityPoints: parseFloat(((data.gpaTotal / data.count) * data.count).toFixed(2)),
      }))
      .sort((a, b) => b.average - a.average);
  }, [validGrades]);

  const performanceTrendData = useMemo(() => {
    return validGrades.map((g, idx) => ({
      name: g.assignmentTitle.length > 15 ? g.assignmentTitle.substring(0, 12) + '...' : g.assignmentTitle,
      assignment: `A${idx + 1}`,
      percentage: g.percentage,
      date: formatDate(g.dueDate || g.gradedAt || g.submittedAt),
    }));
  }, [validGrades]);

  const totalQualityPoints = useMemo(() => {
    return parseFloat(subjectBreakdown.reduce((acc, s) => acc + s.qualityPoints, 0).toFixed(2));
  }, [subjectBreakdown]);

  const handlePrint = () => {
    window.print();
  };

  const handleShare = async () => {
    const studentName = studentData?.fullName || studentData?.name || 'Unknown Student';
    const studentClass = studentData?.class || studentData?.className || 'N/A';
    const summaryText = [
      `Honor Roll Summary`,
      `Student: ${studentName}`,
      `Class: ${studentClass}`,
      `Overall Average: ${overallAverage}%`,
      `GPA: ${overallGpa.toFixed(2)} / 4.0`,
      `Distinction: ${tierConfig.label}`,
      '',
      'Subject Breakdown:',
      ...subjectBreakdown.map(s => `  ${s.name}: ${s.average}% (${s.letterGrade})`),
    ].join('\n');

    try {
      await navigator.clipboard.writeText(summaryText);
      setShareStatus('Copied to clipboard!');
      setTimeout(() => setShareStatus(''), 3000);
    } catch (err) {
      console.error('Failed to copy to clipboard:', err);
      setShareStatus('Failed to copy. Please try again.');
      setTimeout(() => setShareStatus(''), 3000);
    }
  };

  if (loading) {
    return (
      <div className="loading-container">
        <div className="spinner"></div>
        <p>Loading honor roll data...</p>
      </div>
    );
  }

  const studentName = studentData?.fullName || studentData?.name || 'Unknown Student';
  const studentClass = studentData?.class || studentData?.className || 'N/A';

  return (
    <div className="admin-honor-roll-detail">
      <div className="detail-header">
        <button className="back-button" onClick={onBack}>← Back</button>
        <h2>Honor Roll Detail</h2>
      </div>

      {error && <div className="error-message">{error}</div>}

      <div className="student-header-card" style={{ borderLeftColor: tierConfig.color }}>
        <div className="student-header-info">
          <h3>{studentName}</h3>
          <p>Class: {studentClass}</p>
        </div>
        <div
          className="tier-badge"
          style={{ background: tierConfig.bgColor, color: tierConfig.color, borderColor: tierConfig.color }}
        >
          <span className="tier-icon">{tierConfig.icon}</span>
          <span className="tier-label">{tierConfig.label}</span>
        </div>
      </div>

      <div className="action-buttons">
        <button className="action-button print-button" onClick={handlePrint}>
          🖨️ Print Certificate
        </button>
        <button className="action-button share-button" onClick={handleShare}>
          📤 Share
        </button>
        {shareStatus && <span className="share-status">{shareStatus}</span>}
      </div>

      <div className="stats-grid">
        <div className="stat-card">
          <div className="stat-icon">📊</div>
          <div className="stat-info">
            <h3>{overallAverage}%</h3>
            <p>Overall Average</p>
          </div>
        </div>
        <div className="stat-card">
          <div className="stat-icon">🎓</div>
          <div className="stat-info">
            <h3>{overallGpa.toFixed(2)}</h3>
            <p>GPA (4.0 Scale)</p>
          </div>
        </div>
        <div className="stat-card">
          <div className="stat-icon">📚</div>
          <div className="stat-info">
            <h3>{subjectBreakdown.length}</h3>
            <p>Subjects</p>
          </div>
        </div>
        <div className="stat-card">
          <div className="stat-icon">📝</div>
          <div className="stat-info">
            <h3>{validGrades.length}</h3>
            <p>Graded Assignments</p>
          </div>
        </div>
      </div>

      {subjectBreakdown.length === 0 ? (
        <div className="empty-state">
          <div className="empty-icon">📊</div>
          <h3>No Grade Data</h3>
          <p>No grades have been recorded for this student yet.</p>
        </div>
      ) : (
        <>
          <div className="chart-card">
            <h3>Subject Breakdown</h3>
            <div className="subject-breakdown-list">
              {subjectBreakdown.map(subject => (
                <div key={subject.name} className="subject-breakdown-card">
                  <div className="subject-info">
                    <div className="subject-name">{subject.name}</div>
                    <div className="subject-meta">{subject.count} grades</div>
                  </div>
                  <div className="subject-stats">
                    <span className="subject-average">{subject.average}%</span>
                    <span className={`subject-letter letter-${subject.letterGrade.replace(/[+-]/g, '')}`}>
                      {subject.letterGrade}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className="chart-card">
            <h3>GPA Analysis</h3>
            <div className="gpa-analysis-grid">
              <div className="gpa-summary">
                <div className="gpa-summary-item">
                  <span className="gpa-summary-label">Overall GPA</span>
                  <span className="gpa-summary-value">{overallGpa.toFixed(2)}</span>
                </div>
                <div className="gpa-summary-item">
                  <span className="gpa-summary-label">Total Quality Points</span>
                  <span className="gpa-summary-value">{totalQualityPoints}</span>
                </div>
                <div className="gpa-summary-item">
                  <span className="gpa-summary-label">Grading Scale</span>
                  <span className="gpa-summary-value">4.0</span>
                </div>
              </div>
              <div className="gpa-scale-table">
                <table className="gpa-table">
                  <thead>
                    <tr>
                      <th>Letter</th>
                      <th>GPA</th>
                      <th>Range</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr><td>A</td><td>4.0</td><td>93-100</td></tr>
                    <tr><td>A-</td><td>3.7</td><td>90-92</td></tr>
                    <tr><td>B+</td><td>3.3</td><td>87-89</td></tr>
                    <tr><td>B</td><td>3.0</td><td>83-86</td></tr>
                    <tr><td>B-</td><td>2.7</td><td>80-82</td></tr>
                    <tr><td>C+</td><td>2.3</td><td>77-79</td></tr>
                    <tr><td>C</td><td>2.0</td><td>73-76</td></tr>
                    <tr><td>C-</td><td>1.7</td><td>70-72</td></tr>
                    <tr><td>D+</td><td>1.3</td><td>67-69</td></tr>
                    <tr><td>D</td><td>1.0</td><td>63-66</td></tr>
                    <tr><td>D-</td><td>0.7</td><td>60-62</td></tr>
                    <tr><td>F</td><td>0.0</td><td>0-59</td></tr>
                  </tbody>
                </table>
              </div>
            </div>
          </div>

          <div className="chart-card">
            <h3>Performance Trend</h3>
            <ResponsiveContainer width="100%" height={300}>
              <LineChart data={performanceTrendData}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e0e0e0" />
                <XAxis dataKey="assignment" tick={{ fontSize: 12 }} />
                <YAxis domain={[0, 100]} tick={{ fontSize: 12 }} />
                <Tooltip
                  formatter={(value) => [`${value}%`, 'Percentage']}
                  labelFormatter={(label) => {
                    const item = performanceTrendData.find(d => d.assignment === label);
                    return item ? `${item.name} (${item.date})` : label;
                  }}
                />
                <Line type="monotone" dataKey="percentage" stroke="#15803d" strokeWidth={2} name="Percentage" />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </>
      )}
    </div>
  );
}

export default AdminHonorRollDetailView;
